/**
 * Maps MIME-type tails that were incorrectly appended to filenames
 * (e.g. `.vnd.openxmlformats-officedocument.wordprocessingml.document`)
 * to the correct file extension.
 */
const MIME_TAIL_REPLACEMENTS: Array<{ pattern: RegExp; extension: string }> = [
  {
    pattern:
      /\.vnd\.openxmlformats-officedocument\.wordprocessingml\.document$/i,
    extension: 'docx',
  },
  {
    pattern: /\.vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet$/i,
    extension: 'xlsx',
  },
  {
    pattern:
      /\.vnd\.openxmlformats-officedocument\.presentationml\.presentation$/i,
    extension: 'pptx',
  },
  {
    pattern: /\.vnd\.ms-excel$/i,
    extension: 'xls',
  },
  {
    pattern: /\.msword$/i,
    extension: 'doc',
  },
];

function basenameFromPath(value: string): string {
  const withoutQuery = value.split('?')[0]?.split('#')[0] ?? value;
  try {
    return decodeURIComponent(withoutQuery.split('/').pop() || withoutQuery);
  } catch {
    return withoutQuery.split('/').pop() || withoutQuery;
  }
}

/**
 * Soft-fixes download filenames where a MIME subtype was appended as an
 * extension (common blob-store artefact), e.g.
 * `brief.docx.vnd.openxmlformats-officedocument.wordprocessingml.document`
 * → `brief.docx`. Also maps a bare trailing `.document` → `.docx`.
 */
export function sanitizeDownloadFilename(filename: string): string {
  let name = basenameFromPath(filename.trim());
  if (!name) return 'file';

  for (const { pattern, extension } of MIME_TAIL_REPLACEMENTS) {
    if (!pattern.test(name)) continue;
    const stripped = name.replace(pattern, '');
    if (!stripped) return `file.${extension}`;
    if (stripped.toLowerCase().endsWith(`.${extension}`)) return stripped;
    if (/\.[a-z0-9]{1,10}$/i.test(stripped)) return stripped;
    return `${stripped}.${extension}`;
  }

  // Bare leftover: `something.document` → `something.docx`
  if (/\.document$/i.test(name) && !/\.docx$/i.test(name)) {
    return name.replace(/\.document$/i, '.docx');
  }

  return name;
}

export function buildBlobDownloadHref(fileUrl: string, filename?: string): string {
  const preferred = filename?.trim() || basenameFromPath(fileUrl);
  const safeName = sanitizeDownloadFilename(preferred);
  const params = new URLSearchParams({
    url: fileUrl,
    filename: safeName,
  });
  return `/api/download?${params.toString()}`;
}

export function isAzureBlobUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    return new URL(url).hostname.endsWith('.blob.core.windows.net');
  } catch {
    return url.includes('blob.core.windows.net');
  }
}

export function getFileTypeLabel(mimeType?: string, fileName?: string): string {
  if (mimeType) {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('csv') || mimeType === 'text/csv') return 'CSV';
    if (
      mimeType.includes('spreadsheetml') ||
      mimeType.includes('ms-excel') ||
      mimeType === 'application/vnd.ms-excel'
    ) {
      return 'Excel';
    }
    if (mimeType.includes('html')) return 'HTML';
    if (mimeType.includes('screen') || mimeType === 'application/x-screen') return 'Screen';
    if (mimeType.includes('markdown')) return 'MD';
    if (mimeType.startsWith('text/')) return 'Document';
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.includes('wordprocessing') || mimeType.includes('msword')) return 'Document';
    if (
      mimeType.includes('presentationml') ||
      mimeType.includes('powerpoint') ||
      mimeType.includes('presentation')
    ) {
      return mimeType.includes('ms-powerpoint') && !mimeType.includes('openxml') ? 'PPT' : 'PPTX';
    }
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Document';
  }
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (ext === 'csv') return 'CSV';
  if (['xlsx', 'xls'].includes(ext || '')) return 'Excel';
  if (['html', 'htm'].includes(ext || '')) return 'HTML';
  if (ext === 'screen') return 'Screen';
  if (ext === 'md') return 'MD';
  if (['doc', 'docx'].includes(ext || '')) return 'Document';
  if (ext === 'pptx') return 'PPTX';
  if (ext === 'ppt') return 'PPT';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext || '')) return 'Image';
  return 'File';
}
